import { useRouter } from "next/router";
import PropTypes from "prop-types";
import React, { useEffect } from "react";

import DynamicRenderer from "@/components/DynamicRenderer";
import HomepageHero from "@/components/HomepageHero";
import Layout from "@/components/Layout";
import { ACDL_GLOBAL_TRACKING } from "@/components/seo-and-analytics/adobe-data-layer/acdl-data-layer/global-tracking";
import { CONTENT_TYPES } from "@/constants";
import useAcdlGlobalPageTracking from "@/hooks/useAcdlGlobalTracking";
import { getCmsEntries } from "@/server/lib/contentful";
import { pushToHash } from "@/utils/helpers/extract-section-id";

const debug = require("debug")("eqx:pages");

const Page = ({
  adobeAnalytics,
  hero,
  layoutBlock = "start",
  metaData,
  modules = [],
  moduleSpacing,
  navigation,
  notificationBanner,
  showFooter = true,
  showHeader = true,
  slug,
  theme = "light",
}) => {
  const { asPath } = useRouter();

  const scrollFunc = (window, asPath) => {
    if (window) {
      if (window.location.hash) {
        pushToHash(window.location.hash);
      }
    } else {
      pushToHash(asPath);
    }
  };

  useEffect(() => {
    scrollFunc(window, asPath);

    if (window) {
      window.addEventListener("hashchange", () => {
        scrollFunc(window, asPath);
      });
    }

    if (window) {
      return () => {
        window.removeEventListener("hashchange", scrollFunc);
      };
    }
  }, [asPath]);

  useEffect(() => {
    if (asPath.split("?")[0] === "/personaltraining") {
      const timer = setTimeout(() => {
        window.VWO?.event?.("visitedPtPage");
      }, 15000);

      return () => clearTimeout(timer);
    }
  }, [asPath]);

  // ACDL Global Tracking Custom Hook
  useAcdlGlobalPageTracking(
    ACDL_GLOBAL_TRACKING.programmaticUrlRoute,
    metaData?.fields?.title.toLowerCase(),
    undefined,
    adobeAnalytics
  );

  return (
    <Layout
      hideFooter={!showFooter}
      layoutBlock={layoutBlock}
      metaData={metaData?.fields}
      moduleSpacing={moduleSpacing}
      modules={modules}
      navigation={showHeader ? navigation : null}
      notificationBanner={notificationBanner}
      pageSlug={slug}
      theme={theme}
    >
      {hero && <HomepageHero hero={hero} />}

      {modules.map((module, index) => (
        <DynamicRenderer
          {...module?.fields}
          data={module?.fields}
          fileName={module?.sys?.contentType?.sys?.id}
          id={module?.sys?.id}
          key={`${module?.sys?.id}-${index + 1}`}
          page={slug}
        />
      ))}
    </Layout>
  );
};

Page.propTypes = {
  adobeAnalytics: PropTypes.object,
  hero: PropTypes.object,
  layoutBlock: PropTypes.string,
  metaData: PropTypes.object,
  moduleSpacing: PropTypes.string,
  modules: PropTypes.array,
  navigation: PropTypes.object,
  notificationBanner: PropTypes.object,
  showFooter: PropTypes.bool,
  showHeader: PropTypes.bool,
  slug: PropTypes.string,
  theme: PropTypes.string,
};

export default Page;

export const getServerSideProps = async ({ query, resolvedUrl }) => {
  const { slug: dynamicRoute } = query;

  let slug = dynamicRoute;

  if (!slug) {
    slug = resolvedUrl?.split("/").slice(1);
  }

  const pageData = await getCmsEntries({
    contentType: CONTENT_TYPES.PAGE_TEMPLATE,
    filters: {
      "fields.slug": "/" + slug.join("/"),
    },
  });

  if (!pageData?.fields) {
    debug("No matching route for slug:", slug);

    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...pageData.fields,
      slug: slug.join("-"),
    },
  };
};
